import { StreamType } from '../types';
import { ActionFunction1, Action } from 'redux-actions';

type FetcherCallback = ActionFunction1<string, Action<{ streamId: string }>>;

export type StreamItem = number | string | object;

export enum FetchType {
  Next = 'next',
  Previous = 'prev',
  All = 'all',
}

export interface InitializeActionParams {
  streamId: string;
  itemsPerFetch: number;
  groupId?: string;
  streamParams?: object;
  items?: StreamItem[];

  /**
   * The maximum number of items a stream can contain at once. If we surpass this count, we'll
   * drop items from the opposite end of the stream until we're below this limit.
   *
   * Currently only handles fetchNext and fetchPrevious actions
   */
  windowSize?: number;
  hasMore?: boolean;
  hasLess?: boolean;
  total?: number;
  // entities from normalizr, not handled by stream
  entities?: object;
  fetchNextCallback?: FetcherCallback | string;
  fetchPreviousCallback?: FetcherCallback | string;
  fetchAllCallback?: FetcherCallback | string;
}

export interface InitializeAction extends InitializeActionParams {
  type: string;
  streamType: StreamType;
}

export interface FetchAction {
  streamId: string;
  type: string;
  all?: boolean;
}

export interface FetchResult {
  items: StreamItem[];
  hasMore: boolean;
  hasLess?: boolean;
  total?: number;
  // entities from normalizr, not handled by stream
  entities?: object;
}

export interface FetchSuccessActionParams extends FetchResult {
  streamId: string;
  // reset stream contents to the given items
  reset?: boolean;
}

export interface FetchSuccessAction extends FetchSuccessActionParams {
  type: string;
}

export interface FetchPreviousSuccessActionParams {
  streamId: string;
  items: StreamItem[];
  hasLess: boolean;
  dedupeFunction?: (item: StreamItem, existingItems: StreamItem[]) => boolean;
  // entities from normalizr, not handled by stream
  entities?: object;
}

export interface FetchAllSuccessActionParams {
  streamId: string;
  items: StreamItem[];
  // entities from normalizr, not handled by stream
  entities?: object;
}

export interface FetchFailureActionParams {
  streamId: string;
  fetchType: FetchType;
  errorMsg?: string;
}
export interface FetchFailureAction extends FetchFailureActionParams {
  type: string;
}

export interface DeleteItemActionParams {
  streamId: string;
  item: StreamItem;
  /** If true, skip updating the stream's nextOffset by the number of items deleted */
  doNotUpdateNextOffset?: boolean;
  isEqual?: (item1: StreamItem, item2: StreamItem) => boolean;
}

export interface DeleteItemsActionParams {
  streamId: string;
  items: StreamItem[];
  /** If true, skip updating the stream's nextOffset by the number of items deleted */
  doNotUpdateNextOffset?: boolean;
  isEqual?: (item1: StreamItem, item2: StreamItem) => boolean;
}
export interface DeleteItemsAction extends DeleteItemsActionParams {
  type: string;
}

export interface MoveItemActionParams {
  streamId: string;
  item: StreamItem;
  position: number;
}
export interface MoveItemAction extends MoveItemActionParams {
  type: string;
}

export interface DeleteStreamAction {
  streamId: string;
  type: string;
}

export interface ReplaceItemActionParams {
  streamId: string;
  predicate: (item: any) => boolean;
  newItem: StreamItem;
}

export interface ReplaceItemAction extends ReplaceItemActionParams {
  type: string;
}

export interface InsertItemsActionParams {
  streamId: string;
  newItems: {
    item: StreamItem;
    offset: number;
  }[];
  deduplicate?: boolean;
  /** If true, skip updating the stream's nextOffset by the number of items inserted */
  doNotUpdateNextOffset?: boolean;
}

export interface InsertItemsAction extends InsertItemsActionParams {
  type: string;
}

export interface SetParamActionParams {
  streamId: string;
  paramType: string;
  paramValue: any;
}

export interface SetParamAction extends SetParamActionParams {
  type: string;
}

export interface EmptyStreamActionParams {
  streamId: string;
}
export type EmptyStreamAction = Action<EmptyStreamActionParams>;

export interface ResetStreamActionParams {
  streamId: string;
}
export type ResetStreamAction = Action<ResetStreamActionParams>;

export interface BaseStream<TItems = any, TParams extends object = any> {
  streamId: string;
  streamType: StreamType;
  items: TItems[];
  streamParams?: TParams;
  itemsPerFetch: number;
  total?: number;
  isFetching?: boolean;
  hasMore: boolean;
  hasLess: boolean;
  errorMsg?: string;
  groupId?: string;
  failureFetchType?: FetchType;
  fetchFailuresConsecutive?: number;

  /**
   * The maximum number of items a stream can contain at once. If we surpass this count, we'll
   * drop items from the opposite end of the stream until we're below this limit.
   *
   * Currently only handles fetchNext and fetchPrevious actions
   */
  windowSize?: number;

  fetchNextCallback?: string;
  fetchPreviousCallback?: string;
  fetchAllCallback?: string;
}

export interface StreamInitConfig extends Omit<InitializeActionParams, 'streamId'> {
  autoFetch: boolean;
  streamType: StreamType;
}
